import { useState, useEffect } from 'react';
import { CreditCard, Building2, ArrowRightLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaymentMethodSelector({ amount, language, onMethodChange, showComparison = true }) {
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState('card');

  useEffect(() => {
    axios.get(`${API}/payment-settings/public`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  // Auto-select the only enabled method
  useEffect(() => {
    if (!settings) return;
    const enabled = [];
    if (settings.card_enabled) enabled.push('card');
    if (settings.ach_enabled) enabled.push('ach');
    if (settings.zelle_enabled) enabled.push('zelle');
    if (enabled.length === 1) {
      setSelected(enabled[0]);
    } else if (enabled.length > 0 && !enabled.includes(selected)) {
      setSelected(enabled[0]);
    }
  }, [settings]);

  useEffect(() => {
    if (onMethodChange && settings) {
      const discount = selected === 'ach' ? (settings?.ach_discount_pct ?? 0) :
                        selected === 'zelle' ? (settings?.zelle_discount_pct ?? 0) : 0;
      onMethodChange(selected, discount);
    }
  }, [selected, settings]);

  if (!settings) return null;

  const achPct = settings.ach_discount_pct ?? 0;
  const zellePct = settings.zelle_discount_pct ?? 0;
  const cardPrice = amount;
  const achPrice = Math.round(amount * (1 - achPct / 100) * 100) / 100;
  const zellePrice = Math.round(amount * (1 - zellePct / 100) * 100) / 100;
  const achSave = Math.round((cardPrice - achPrice) * 100) / 100;
  const zelleSave = Math.round((cardPrice - zellePrice) * 100) / 100;

  const methods = [];

  if (settings.zelle_enabled) {
    methods.push({
      id: 'zelle',
      icon: <ArrowRightLeft className="h-5 w-5" />,
      label: language === 'es' ? 'Zelle — Pago directo' : 'Zelle — Direct payment',
      subtitle: zellePct > 0
        ? (language === 'es' ? `${zellePct}% de descuento — Sin comisiones` : `${zellePct}% discount — No processing fees`)
        : (language === 'es' ? 'Transferencia directa — Sin comisiones' : 'Direct transfer — No processing fees'),
      badge: zellePct > 0 ? (language === 'es' ? 'MAYOR AHORRO' : 'BEST SAVINGS') : null,
      badgeColor: 'bg-[#6B21A8]',
      price: zellePrice,
      borderColor: selected === 'zelle' ? 'border-[#6B21A8] ring-2 ring-[#6B21A8]/20' : 'border-black/10 hover:border-[#6B21A8]/40',
    });
  }

  if (settings.ach_enabled) {
    methods.push({
      id: 'ach',
      icon: <Building2 className="h-5 w-5" />,
      label: language === 'es' ? 'Transferencia bancaria ACH' : 'ACH Bank transfer',
      subtitle: achPct > 0
        ? (language === 'es' ? `${achPct}% de descuento` : `${achPct}% discount`)
        : (language === 'es' ? 'Transferencia bancaria' : 'Bank transfer'),
      badge: achPct > 0 ? (language === 'es' ? `AHORRA ${achPct}%` : `SAVE ${achPct}%`) : null,
      badgeColor: 'bg-[#007AFF]',
      price: achPrice,
      borderColor: selected === 'ach' ? 'border-[#007AFF] ring-2 ring-[#007AFF]/20' : 'border-black/10 hover:border-[#007AFF]/40',
    });
  }

  if (settings.card_enabled) {
    methods.push({
      id: 'card',
      icon: <CreditCard className="h-5 w-5" />,
      label: language === 'es' ? 'Tarjeta de crédito o débito' : 'Credit or debit card',
      subtitle: language === 'es'
        ? 'Pago seguro con Stripe'
        : 'Secure payment with Stripe',
      badge: null,
      badgeColor: '',
      price: cardPrice,
      borderColor: selected === 'card' ? 'border-[#E63946] ring-2 ring-[#E63946]/20' : 'border-black/10 hover:border-black/20',
    });
  }

  // Count enabled methods
  const enabledCount = [settings.card_enabled, settings.ach_enabled, settings.zelle_enabled].filter(Boolean).length;

  return (
    <div className="space-y-3" data-testid="payment-method-selector">
      {enabledCount > 1 && (
        <>
          <p className="text-sm font-semibold text-[#1D1D1F] mb-2">
            {language === 'es' ? 'Método de pago' : 'Payment method'}
          </p>

          {methods.map((m) => (
            <button
              key={m.id}
              type="button"
              data-testid={`payment-method-${m.id}`}
              onClick={() => setSelected(m.id)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${m.borderColor} ${selected === m.id ? 'bg-white shadow-sm' : 'bg-white/50'}`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 flex items-center justify-center w-5 h-5 rounded-full border-2 flex-shrink-0 ${selected === m.id ? 'border-[#E63946] bg-[#E63946]' : 'border-[#86868B]/40'}`}>
                  {selected === m.id && <div className="w-2 h-2 bg-white rounded-full" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[#86868B]">{m.icon}</span>
                    <span className="font-medium text-[#1D1D1F] text-sm">{m.label}</span>
                    {m.badge && (
                      <span className={`text-[10px] font-bold text-white px-2 py-0.5 rounded-full ${m.badgeColor}`}>
                        {m.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#86868B] mt-0.5">{m.subtitle}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-[#1D1D1F]">${m.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
              </div>
            </button>
          ))}

          {showComparison && amount > 0 && (
            <div className="mt-3 p-3 bg-[#F5F5F7] rounded-xl" data-testid="payment-method-comparison">
              <p className="text-xs text-[#86868B]">
                {language === 'es' ? 'Comparación:' : 'Comparison:'}{' '}
                {settings.card_enabled && <><span className="font-medium text-[#1D1D1F]">{language === 'es' ? 'Tarjeta' : 'Card'} ${cardPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span> · </>}
                {settings.ach_enabled && <><span className="font-medium text-[#007AFF]">ACH ${achPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span> <span className="text-[#34C759]">({language === 'es' ? 'ahorras' : 'save'} ${achSave.toFixed(2)})</span> · </>}
                {settings.zelle_enabled && <><span className="font-medium text-[#6B21A8]">Zelle ${zellePrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span> <span className="text-[#34C759]">({language === 'es' ? 'ahorras' : 'save'} ${zelleSave.toFixed(2)})</span></>}
              </p>
            </div>
          )}

          <p className="text-[10px] text-[#86868B] italic">
            {language === 'es'
              ? 'Ahorra pagando por ACH o Zelle. Los precios publicados son la tarifa estándar (tarjeta).'
              : 'Save by paying via ACH or Zelle. Published prices are the standard rate (card).'}
          </p>
        </>
      )}

      {enabledCount === 1 && methods.length > 0 && (
        <div className="p-4 bg-[#F5F5F7] rounded-xl" data-testid="single-payment-method">
          <div className="flex items-center gap-3">
            <span className="text-[#86868B]">{methods[0].icon}</span>
            <div className="flex-1">
              <p className="font-medium text-sm text-[#1D1D1F]">{methods[0].label}</p>
              <p className="text-xs text-[#86868B]">{methods[0].subtitle}</p>
            </div>
            <p className="font-bold text-[#1D1D1F]">${methods[0].price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function ZelleInstructionsPanel({ amount, studentNames, language, settings }) {
  const recipient = settings?.zelle_recipient || 'ARCE SPANISH SCHOOL, LLC.';
  const qrUrl = settings?.zelle_qr_url || '';

  return (
    <div className="p-5 bg-[#6B21A8]/5 border-2 border-[#6B21A8]/20 rounded-2xl space-y-4" data-testid="zelle-instructions-panel">
      <div className="flex items-center gap-2">
        <ArrowRightLeft className="h-5 w-5 text-[#6B21A8]" />
        <h3 className="font-semibold text-[#1D1D1F]">
          {language === 'es' ? 'Instrucciones de pago Zelle' : 'Zelle payment instructions'}
        </h3>
      </div>

      <div className="space-y-3 text-sm">
        <div className="flex justify-between items-center p-3 bg-white rounded-xl">
          <span className="text-[#86868B]">{language === 'es' ? 'Envía el pago a' : 'Send payment to'}:</span>
          <span className="font-semibold text-[#1D1D1F]">{recipient}</span>
        </div>
        <div className="flex justify-between items-center p-3 bg-white rounded-xl">
          <span className="text-[#86868B]">{language === 'es' ? 'Monto a pagar' : 'Amount to pay'}:</span>
          <span className="font-bold text-xl text-[#6B21A8]">${amount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between items-start p-3 bg-white rounded-xl">
          <span className="text-[#86868B]">{language === 'es' ? 'Referencia' : 'Reference'}:</span>
          <span className="font-medium text-[#1D1D1F] text-right">{studentNames}<br />{language === 'es' ? 'Matrícula 2026-2027' : 'Enrollment 2026-2027'}</span>
        </div>
      </div>

      {qrUrl && (
        <div className="flex justify-center">
          <img src={qrUrl} alt="Zelle QR Code" className="w-40 h-40 rounded-xl border border-black/5" />
        </div>
      )}

      <div className="p-3 bg-[#FF9500]/10 rounded-xl text-xs text-[#1D1D1F]">
        <p className="font-semibold mb-1">{language === 'es' ? 'Importante:' : 'Important:'}</p>
        <p>{language === 'es'
          ? 'Tu inscripción será confirmada en 24–48h tras la verificación del pago. El acuerdo de matrícula ya ha sido firmado.'
          : 'Your enrollment will be confirmed within 24–48h after payment verification. The enrollment agreement has already been signed.'}</p>
      </div>
    </div>
  );
}
